import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { isFunction } from 'radash';
import { useCallback, useEffect, useState } from 'react';

import { HeadlessCombobox, HeadlessComboboxOption } from '@/components/core/HeadlessCombobox';
import { Icon } from '@/components/core/Icon';
import { Tooltip } from '@/components/core/Tooltip';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import useFocusOnAttribute from '../../hooks/useFocusOnAttribute';
import { useReferenceItemFormatter } from '../../hooks/useReferenceItemFormatter';
import { useReferencedItems } from '../../queries';

interface ReferenceItemOption extends PersistedItem {
    key?: string;
    label?: string;
}

interface FindItemProps {
    attribute: DataModelAttribute;
    readonly: boolean;
    placeholder: string;
    onItemSelected: (item: ReferenceItemOption) => void;
    autoSelect?: boolean;
    preselectedItem?: ReferenceItemOption | null;
    containingItem: PersistedItem;
}

export const FindItem = ({
    attribute,
    readonly,
    placeholder,
    onItemSelected,
    autoSelect = true,
    preselectedItem,
    containingItem,
}: FindItemProps) => {
    const { t } = useTranslation();
    const $angularJsInjector = getInjector();
    const $rootScope = getRootScope($angularJsInjector);

    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);
    const { formatReferenceItem } = useReferenceItemFormatter(attribute);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [manualInput, setManualInput] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<ReferenceItemOption | null>(null);
    const { data: optionsOfReferenceItems, isLoading: loadingOptions } = useReferencedItems({
        variables: {
            keyword: searchKeyword,
            referenceableCategories: attribute.params?.references || attribute.params?.referenceCategories,
        },
        retry: false,
        enabled: searchKeyword != ''
    });

    const handleSearchKeywordChange = useCallback((keyword: string) => {
        const searchQueryCustomFunction = $rootScope?.getService(attribute?.params?.uiSearchQuery);

        if (isFunction(searchQueryCustomFunction)) {
            keyword = searchQueryCustomFunction(keyword, containingItem, attribute, $rootScope.user, $rootScope.organization);
        }
        setSearchKeyword(keyword);
    },
    [$rootScope, attribute, containingItem],
    );

    useEffect(() => {
        if (preselectedItem) {
            setSelectedItem(preselectedItem);
            onItemSelected(preselectedItem);
        }
    }, [preselectedItem, onItemSelected]);

    const handleItemChange = (referenceItem: ReferenceItemOption | null) => {
        if (referenceItem) {
            handleSearchKeywordChange('');
            setSelectedItem(referenceItem);
            onItemSelected(referenceItem);
            // Only blur the combobox when autoSelect is true
            if (autoSelect && focusRef.current) {
                focusRef.current.blur();
            }
        }
    };

    const handleInputChange = (input: string) => {
        if (input.length > 2) {
            handleSearchKeywordChange(input);
        }
    };
    const isDisabled = readonly || !!preselectedItem;
    return (
        <div className="tw-mt-5 tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-items-start md:tw-gap-4 md:tw-justify-end">
            <div className='tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 md:tw-justify-end md:tw-w-40'>
                <span className="tw-flex tw-items-center tw-text-t-primary tw-text-t-sm lg-font-dynamic">{t('FIND_ITEM')}</span>
                <span className="tw-inline-block tw-h-5 tw-w-5"><Tooltip popupContent={t('FIND_ITEM.TOOLTIP')} /></span>
            </div>
            <div className="tw-flex tw-items-center tw-flex-1 tw-flex-col tw-gap-3">
                <div className="tw-w-full" title={(selectedItem && formatReferenceItem(selectedItem, {} as PersistedItem)) || placeholder}>
                    <HeadlessCombobox
                        id={'find_item'}
                        isDisabled={isDisabled}
                        ref={focusRef}
                        placeholder={(selectedItem && formatReferenceItem(selectedItem, {} as PersistedItem)) || placeholder}
                        isLoading={loadingOptions}
                        options={optionsOfReferenceItems?.map(item => ({
                            key: item.primaryKey__,
                            label: formatReferenceItem(item, {} as PersistedItem),
                            value: item,
                            ...item
                        }) as HeadlessComboboxOption) || []}
                        filterOption={() => true}
                        value={
                            manualInput !== null
                                ? { key: 'manual', label: manualInput, value: null }
                                : selectedItem
                                    ? {
                                        key: selectedItem.primaryKey__,
                                        label: formatReferenceItem(selectedItem, {} as PersistedItem) || '',
                                        value: selectedItem
                                    }
                                    : null
                        }
                        onInputChange={(input) => {
                            setManualInput(input);
                            handleInputChange(input);
                        }}
                        onInputClick={() => {
                            if (selectedItem) {
                                setManualInput('');
                                handleSearchKeywordChange('');
                            }
                        }}
                        onChange={(option) => {
                            setManualInput(null);
                            handleItemChange(option?.value || null);
                        }}
                        renderButton={() => { return !isDisabled ? <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2">
                            <Icon icon={MagnifyingGlassIcon} size="sm" variant="dark" />
                        </div>: null; }
                        }
                    />
                </div>

                <div className="tw-w-full tw-h-auto tw-min-h-[128px] tw-rounded tw-border tw-px-2 tw-bg-fill-secondary tw-mb-4 lg:tw-min-h-[144px] tw-text-t-sm lg-font-dynamic">
                    {selectedItem ? (
                        <div className="tw-flex tw-flex-col tw-min-h-32 tw-min-h-[128px] lg:tw-min-h-[144px]">
                            <div className="tw-text-t-primary tw-flex tw-justify-start tw-my-auto">
                                <span>
                                    {formatReferenceItem(selectedItem, {} as PersistedItem)
                                        ?.split(', ')
                                        .map((part, index) => (
                                            <div key={index} className="tw-gap-1">
                                                {part.trim()}
                                            </div>
                                        ))}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-[--gray-500] tw-min-h-[128px] lg:tw-min-h-[144px]">
                            {t('NOTHING_SELECTED')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
